import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo } from "react";
import { createKPI } from "../types/kpi";
import { Button, Checkbox, TextInput, Modal } from "@perfectserve/library";
import { CSSProvider } from "@perfectserve/css-styles-react";
const KPISelectorModal = ({ isOpen, onClose, selectedKPI, savedKPIs: _savedKPIs, interactions, onKPIChange, onSaveKPI, }) => {
    const [editingKPI, setEditingKPI] = useState(null);
    const [selectedOriginators, setSelectedOriginators] = useState([]);
    const [selectedPriorities, setSelectedPriorities] = useState([]);
    const [selectedReasons, setSelectedReasons] = useState([]);
    const [requiresCallback, setRequiresCallback] = useState(false);
    const [controlledTransfersOnly, setControlledTransfersOnly] = useState(false);
    const [blindTransfersOnly, setBlindTransfersOnly] = useState(false);
    const [handoffTransfersOnly, setHandoffTransfersOnly] = useState(false);
    const [excellentThreshold, setExcellentThreshold] = useState(5);
    const [goodThreshold, setGoodThreshold] = useState(15);
    const [acceptableThreshold, setAcceptableThreshold] = useState(30);
    const kpiOptions = useMemo(() => {
        const originators = new Set();
        const priorities = new Set();
        const reasons = new Set();
        interactions.forEach((interaction) => {
            if (!interaction)
                return;
            if (interaction.ToFilterOriginator) {
                originators.add(interaction.ToFilterOriginator);
            }
            if (interaction.ToFilterPriority) {
                priorities.add(interaction.ToFilterPriority);
            }
            if (interaction.ToFilterReason) {
                reasons.add(interaction.ToFilterReason);
            }
        });
        const result = {
            originators: Array.from(originators).sort(),
            priorities: Array.from(priorities).sort(),
            reasons: Array.from(reasons).sort(),
        };
        return result;
    }, [interactions]);
    const openModal = (kpi) => {
        if (kpi) {
            setEditingKPI(kpi);
            setSelectedOriginators(kpi.filters.originators || []);
            setSelectedPriorities(kpi.filters.priorities || []);
            setSelectedReasons(kpi.filters.reasons || []);
            setRequiresCallback(kpi.filters.requiresCallback);
            setControlledTransfersOnly(kpi.filters.controlledTransfersOnly || false);
            setBlindTransfersOnly(kpi.filters.blindTransfersOnly || false);
            setHandoffTransfersOnly(kpi.filters.handoffTransfersOnly || false);
            setExcellentThreshold(kpi.thresholds.excellent);
            setGoodThreshold(kpi.thresholds.good);
            setAcceptableThreshold(kpi.thresholds.acceptable);
        }
        else {
            setEditingKPI(null);
            setSelectedOriginators([]);
            setSelectedPriorities([]);
            setSelectedReasons([]);
            setRequiresCallback(false);
            setControlledTransfersOnly(false);
            setBlindTransfersOnly(false);
            setHandoffTransfersOnly(false);
            setExcellentThreshold(5);
            setGoodThreshold(15);
            setAcceptableThreshold(30);
        }
    };
    const closeModal = () => {
        onClose();
        setEditingKPI(null);
    };
    const handleSave = () => {
        const autoGeneratedName = generateKPIName();
        const filters = {
            requiresCallback,
            controlledTransfersOnly,
            blindTransfersOnly,
            handoffTransfersOnly,
        };
        if (selectedOriginators.length > 0) {
            filters.originators = selectedOriginators;
        }
        if (selectedPriorities.length > 0) {
            filters.priorities = selectedPriorities;
        }
        if (selectedReasons.length > 0) {
            filters.reasons = selectedReasons;
        }
        const thresholds = {
            excellent: excellentThreshold,
            good: goodThreshold,
            acceptable: acceptableThreshold,
        };
        const kpi = editingKPI
            ? {
                ...editingKPI,
                name: autoGeneratedName,
                filters,
                thresholds,
            }
            : createKPI(autoGeneratedName, filters, thresholds);
        onSaveKPI(kpi);
        onKPIChange(kpi);
        closeModal();
    };
    const generateKPIName = () => {
        const parts = [];
        if (selectedPriorities.length > 0)
            parts.push(selectedPriorities.join("/"));
        if (selectedOriginators.length > 0)
            parts.push(selectedOriginators.join("/"));
        if (requiresCallback)
            parts.push("Callbacks");
        if (controlledTransfersOnly)
            parts.push("Controlled Transfers");
        if (blindTransfersOnly)
            parts.push("Blind Transfers");
        if (handoffTransfersOnly)
            parts.push("Handoff Transfers");
        if (selectedReasons.length > 0)
            parts.push(selectedReasons.join("/"));
        return parts.length > 0 ? parts.join(" ") : "Custom KPI";
    };
    const handleOriginatorChange = (originator, checked) => {
        if (checked) {
            setSelectedOriginators([...selectedOriginators, originator]);
        }
        else {
            setSelectedOriginators(selectedOriginators.filter((o) => o !== originator));
        }
    };
    const handlePriorityChange = (priority, checked) => {
        if (checked) {
            setSelectedPriorities([...selectedPriorities, priority]);
        }
        else {
            setSelectedPriorities(selectedPriorities.filter((p) => p !== priority));
        }
    };
    const handleReasonChange = (reason, checked) => {
        if (checked) {
            setSelectedReasons([...selectedReasons, reason]);
        }
        else {
            setSelectedReasons(selectedReasons.filter((r) => r !== reason));
        }
    };
    React.useEffect(() => {
        if (isOpen) {
            openModal(selectedKPI);
        }
    }, [isOpen, selectedKPI]);
    if (!isOpen) {
        return null;
    }
    return (_jsx(Modal, { isOpen: isOpen, onClose: closeModal, title: "Create New KPI", maxWidth: "600px", children: _jsx(CSSProvider, { children: _jsxs("div", { className: "p-20", children: [_jsx("p", { children: "Select options below to create your custom KPI metric. The name will be automatically generated based on your selections." }), _jsxs("div", { className: "kpi-section", children: [_jsx("h4", { children: "Priority" }), _jsx("div", { className: "kpi-options", children: kpiOptions.priorities.length === 0 ? (_jsx("em", { children: "No priorities available" })) : (kpiOptions.priorities.map((priority, index) => (_jsx(Checkbox, { checked: selectedPriorities.includes(priority), onChange: (checked) => handlePriorityChange(priority, checked), label: priority }, `${priority}-${index}`)))) })] }), _jsxs("div", { className: "kpi-section", children: [_jsx("h4", { children: "Originator" }), _jsx("div", { className: "kpi-options", children: kpiOptions.originators.length === 0 ? (_jsx("div", { className: "kpi-notice", children: _jsx("em", { children: "No originator data available in current interactions. This is an optional field that may not be populated for all interactions." }) })) : (kpiOptions.originators.map((originator, index) => (_jsx(Checkbox, { checked: selectedOriginators.includes(originator), onChange: (checked) => handleOriginatorChange(originator, checked), label: originator }, `${originator}-${index}`)))) })] }), _jsxs("div", { className: "kpi-section", children: [_jsx("h4", { children: "Reason (Optional)" }), _jsx("div", { className: "kpi-options", children: kpiOptions.reasons.length === 0 ? (_jsx("div", { className: "kpi-notice", children: _jsx("em", { children: "No reason data available in current interactions. This is an optional field that may not be populated for all interactions." }) })) : (kpiOptions.reasons.map((reason, index) => (_jsx(Checkbox, { checked: selectedReasons.includes(reason), onChange: (checked) => handleReasonChange(reason, checked), label: reason }, `${reason}-${index}`)))) })] }), _jsxs("div", { className: "kpi-section", children: [_jsx("h4", { children: "Features" }), _jsx(Checkbox, { checked: requiresCallback, onChange: setRequiresCallback, label: "Include only interactions with callbacks" }), _jsxs("div", { className: "d-flex", style: { gap: "20px", marginTop: "10px" }, children: [_jsx(Checkbox, { checked: controlledTransfersOnly, onChange: setControlledTransfersOnly, label: "Controlled Transfers" }), _jsx(Checkbox, { checked: blindTransfersOnly, onChange: setBlindTransfersOnly, label: "Blind Transfers" }), _jsx(Checkbox, { checked: handoffTransfersOnly, onChange: setHandoffTransfersOnly, label: "Handoff Transfers" })] })] }), _jsxs("div", { className: "kpi-section", children: [_jsx("h4", { children: "Response Time Thresholds" }), _jsxs("div", { className: "kpi-thresholds", children: [_jsx(TextInput, { type: "number", label: `Excellent (< ${excellentThreshold} min)`, value: excellentThreshold, onChange: (value) => setExcellentThreshold(parseInt(value) || 5), min: "1" }), _jsx(TextInput, { type: "number", label: `Good (< ${goodThreshold} min)`, value: goodThreshold, onChange: (value) => setGoodThreshold(parseInt(value) || 15), min: "1" }), _jsx(TextInput, { type: "number", label: `Acceptable (< ${acceptableThreshold} min)`, value: acceptableThreshold, onChange: (value) => setAcceptableThreshold(parseInt(value) || 30), min: "1" })] }), _jsx("p", { children: _jsxs("small", { children: ["Times above ", acceptableThreshold, " minutes will be marked as ", _jsx("strong", { children: "Poor" })] }) })] }), _jsxs("div", { className: "d-flex justify-content-end mt-25 pt-15", style: {
                            gap: "12px",
                            borderTop: "1px solid var(--ps-gray4)",
                        }, children: [_jsx(Button, { onClick: () => {
                                    onKPIChange(undefined);
                                    closeModal();
                                }, variant: "danger", children: "Clear KPI" }), _jsx(Button, { onClick: closeModal, children: "Cancel" }), _jsx(Button, { onClick: handleSave, variant: "primary", children: "Create KPI" })] })] }) }) }));
};
export default KPISelectorModal;
//# sourceMappingURL=KPISelectorModal.js.map